import React, { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";

import { IMAGE_ACCEPT, sanitizeBrandKey, validateImageFile } from "./images";
import {
  catalogEditsNeedReview,
  extractApiError,
  useCatalogApi,
} from "./useCatalogApi";

/**
 * Reusable image manager for a product or brand. Lets the user set an image by
 * pasting a URL, choosing a file, or dragging & dropping. On save the products
 * API copies the image into the CDN (product-images/{foodId} or
 * brand-images/{brandSlug}); re-uploading replaces it. Reads the current image
 * on mount via the batch lookup so an existing image shows immediately.
 *
 * Linking is convention-based:
 *  - kind="product": keyed by the food id (stable).
 *  - kind="brand":   keyed by sanitizeBrandKey(brandName); a live slug preview is
 *                    shown so it's clear which brand key an image is stored under.
 */
export interface ImageInputProps {
  kind: "product" | "brand";
  /** product: the food id. brand: the raw (free-form) brand name. */
  keyValue: string;
  /** Disable all interaction (e.g. product with no id yet). */
  disabled?: boolean;
  /** Shown when disabled to explain why (e.g. "Save the product first"). */
  disabledReason?: string;
  className?: string;
  /** Notified when the stored image changes (new url or null on remove). */
  onChange?: (url: string | null) => void;
  /**
   * For kind="brand": the product being edited. When set, attaching a brand
   * image also stamps a catalog:edit origin on this food id server-side, so the
   * product appears in the customer's contributions list.
   */
  claimFoodId?: string;
  /**
   * Whether an existing image may be replaced or cleared. Adding an image when
   * none exists is always allowed. Defaults to true for products; brand logos
   * default to staff-only when edits need review.
   */
  canReplace?: boolean;
}

export const ImageInput: React.FC<ImageInputProps> = ({
  kind,
  keyValue,
  disabled,
  disabledReason,
  className = "",
  onChange,
  claimFoodId,
  canReplace,
}) => {
  const api = useCatalogApi();
  const fileRef = useRef<HTMLInputElement>(null);

  const trimmedKey = (keyValue || "").trim();
  const slug = kind === "brand" ? sanitizeBrandKey(trimmedKey) : trimmedKey;
  const isDisabled = !!disabled || slug === "";
  const mayReplace =
    canReplace ?? (kind === "brand" ? !catalogEditsNeedReview() : true);

  const [current, setCurrent] = useState<string | null>(null);
  const [urlDraft, setUrlDraft] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState("");
  // "" | loading (initial fetch) | uploading | removing
  const [status, setStatus] = useState<
    "" | "loading" | "uploading" | "removing"
  >("");
  const busy = status === "uploading" || status === "removing";
  // An image already exists and this caller may not change it.
  const locked = !!current && !mayReplace;

  // Keep the latest onChange in a ref so `notify` is stable — otherwise an
  // inline parent callback would re-run the load effect (and re-lookup) on every
  // render.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  const notify = useCallback((url: string | null) => {
    onChangeRef.current?.(url);
  }, []);

  // Load the current image whenever the key changes. For brands (key changes as
  // the user types the name) this is debounced so we don't lookup per keystroke.
  // We notify the parent with the loaded url (or null) so it can mirror the
  // image elsewhere (e.g. a brand thumbnail) without a second lookup.
  useEffect(() => {
    if (isDisabled) {
      setCurrent(null);
      notify(null);
      return undefined;
    }
    let cancelled = false;
    const run = async () => {
      setStatus("loading");
      try {
        const res = await api.lookupImages(
          kind === "product"
            ? { productIds: [trimmedKey] }
            : { brandNames: [trimmedKey] }
        );
        const url =
          kind === "product"
            ? res.products[trimmedKey]
            : res.brands[trimmedKey];
        if (!cancelled) {
          setCurrent(url || null);
          notify(url || null);
        }
      } catch {
        if (!cancelled) {
          setCurrent(null);
          notify(null);
        }
      } finally {
        if (!cancelled) setStatus("");
      }
    };
    const timer = window.setTimeout(run, kind === "brand" ? 400 : 0);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // trimmedKey drives the product case; slug re-runs when the brand slug changes.
  }, [api, kind, trimmedKey, slug, isDisabled, notify]);

  const doUpload = useCallback(
    async (input: File | string) => {
      if (isDisabled) return;
      setError("");
      setStatus("uploading");
      try {
        const res =
          kind === "product"
            ? await api.uploadProductImage(trimmedKey, input)
            : await api.uploadBrandImage(
                trimmedKey,
                input,
                (claimFoodId || "").trim() || undefined
              );
        setCurrent(res.url);
        setUrlDraft("");
        notify(res.url);
        toast.success(
          kind === "brand" ? "Brand image saved" : "Product image saved"
        );
      } catch (err) {
        const msg = extractApiError(err, "Failed to save image");
        setError(msg);
        toast.error(msg);
      } finally {
        setStatus("");
      }
    },
    [api, kind, trimmedKey, isDisabled, notify, claimFoodId]
  );

  const handleFiles = useCallback(
    (files: FileList | null) => {
      const file = files && files[0];
      if (!file) return;
      const problem = validateImageFile(file);
      if (problem) {
        setError(problem);
        return;
      }
      void doUpload(file);
    },
    [doUpload]
  );

  const submitUrl = useCallback(() => {
    const u = urlDraft.trim();
    if (!u) return;
    void doUpload(u);
  }, [urlDraft, doUpload]);

  const doRemove = useCallback(async () => {
    if (isDisabled || !current) return;
    setError("");
    setStatus("removing");
    try {
      if (kind === "product") await api.deleteProductImage(trimmedKey);
      else await api.deleteBrandImage(trimmedKey);
      setCurrent(null);
      notify(null);
      toast.success("Image removed");
    } catch (err) {
      const msg = extractApiError(err, "Failed to remove image");
      setError(msg);
      toast.error(msg);
    } finally {
      setStatus("");
    }
  }, [api, kind, trimmedKey, current, isDisabled, notify]);

  const openPicker = () => {
    if (isDisabled || busy || locked) return;
    fileRef.current?.click();
  };

  const noun = kind === "brand" ? "Brand image" : "Product image";

  return (
    <div className={className}>
      <div
        onClick={!current && !isDisabled ? openPicker : undefined}
        onDragOver={(e) => {
          if (isDisabled || locked) return;
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          if (isDisabled || locked) return;
          e.preventDefault();
          setDragOver(false);
          handleFiles(e.dataTransfer.files);
        }}
        className={`group relative flex items-center gap-3 rounded-xl border p-2.5 transition ${
          dragOver
            ? "border-indigo-400 bg-indigo-50 ring-2 ring-indigo-100"
            : "border-gray-200 bg-white hover:border-gray-300"
        } ${isDisabled ? "opacity-60" : ""} ${
          !current && !isDisabled ? "cursor-pointer" : ""
        }`}
      >
        {/* Thumbnail / placeholder */}
        <div className="relative flex h-16 w-16 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
          {status === "loading" ? (
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-indigo-600" />
          ) : current ? (
            <img
              src={current}
              alt={`${noun} preview`}
              className="h-full w-full object-contain"
            />
          ) : (
            <svg
              className="h-6 w-6 text-gray-300"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
              />
            </svg>
          )}
          {busy && (
            <span className="absolute inset-0 flex items-center justify-center bg-white/70">
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-indigo-600" />
            </span>
          )}
        </div>

        {/* Controls */}
        {locked ? (
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold text-gray-700">{noun} is set</p>
            <p className="mt-0.5 text-[11px] leading-snug text-gray-500">
              {kind === "brand"
                ? "Brand logos are shared by every product from this brand, so only Passio can change one that's already set. Contact Passio if it's wrong."
                : "You can add a photo to any product, but only replace the photo on products you added. Contact Passio if this one is wrong."}
            </p>
          </div>
        ) : (
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  openPicker();
                }}
                disabled={isDisabled || busy}
                className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-semibold text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {current ? "Replace" : "Upload"}
              </button>
              {current && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    void doRemove();
                  }}
                  disabled={isDisabled || busy}
                  className="rounded-md border border-transparent px-2 py-1 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {status === "removing" ? "Clearing…" : "Clear"}
                </button>
              )}
              {status === "uploading" && (
                <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                  Saving…
                </span>
              )}
            </div>

            {/* URL entry */}
            <div
              className="mt-1.5 flex items-center gap-1.5"
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="url"
                value={urlDraft}
                onChange={(e) => setUrlDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    submitUrl();
                  }
                }}
                disabled={isDisabled || busy}
                placeholder="or paste an image URL…"
                className="min-w-0 flex-1 rounded-md border border-gray-200 px-2 py-1 text-xs focus:border-indigo-500 focus:outline-none disabled:opacity-50"
              />
              <button
                type="button"
                onClick={submitUrl}
                disabled={isDisabled || busy || !urlDraft.trim()}
                className="flex-shrink-0 rounded-md bg-indigo-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Fetch
              </button>
            </div>

            <p className="mt-1 text-[11px] leading-tight text-gray-400">
              Drag, drop, or paste a URL — most types accepted. Saves instantly,
              no need to save the record.
              {kind === "brand" && slug ? (
                <>
                  {" "}
                  Brand key{" "}
                  <span className="font-mono text-gray-500">{slug}</span>.
                </>
              ) : null}
            </p>
          </div>
        )}

        <input
          ref={fileRef}
          type="file"
          accept={IMAGE_ACCEPT}
          className="hidden"
          onChange={(e) => {
            handleFiles(e.target.files);
            // reset so choosing the same file again re-fires change
            e.target.value = "";
          }}
        />
      </div>

      {isDisabled && disabledReason && (
        <p className="mt-1 text-[11px] text-amber-600">{disabledReason}</p>
      )}
      {error && <p className="mt-1 text-[11px] text-red-600">{error}</p>}
    </div>
  );
};

export default ImageInput;
